import type { SyntheticEvent } from "react";
import { LocalizationProvider } from '@mui/x-date-pickers-v.6/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers-v.6/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers-v.6/DatePicker';
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { getFormInputProps } from "util/forms";
import { selectECommerceShoppingPlatforms } from "features/general/selectors";
import { UrlTextField } from "ui/atoms/TextField";
import { CountryIso2 } from "ui/widgets/CountrySelector";
import OnboardingForm from "ui/forms/Onboarding/OnboardingForm";
import StatefulSelector from "ui/widgets/StatefulSelector";
import { FormField, OnboardingBusinessInformationProps } from "./types";
import OnboardingReviewableFormInput from "../../OnboardingReviewableFormInput";

const BusinessInformation = ({
    values,
    isSubmitting,
    isValid,
    isBootstrapDataLoading,
    bootstrapData,
    setFieldValue,
    setFieldTouched,
    handleChange,
    handleBlur,
    handleSubmit,
    ...form
}: OnboardingBusinessInformationProps) => {
    const onAutocompleteChange = (formField: string) =>
        (_: SyntheticEvent, values: string | Array<string>) => {
            setFieldValue(formField, values, true);
            setTimeout(() => setFieldTouched(formField, true));
        };

    return (
        <OnboardingForm
            {...form}
            isSubmitting={isSubmitting}
            isValid={isValid}
            handleSubmit={handleSubmit}
        >
            <Typography
                variant='h6'
                gutterBottom
            >
                Company Details
            </Typography>
            <OnboardingReviewableFormInput
                name={FormField.CompanyName}
                values={values}
                renderLabel={() => 'Company Name'}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                renderFormInput={({ reviewableStatus, ...restFormInput }) => (
                    <TextField
                        {...restFormInput}
                        {...getFormInputProps(FormField.CompanyName, form)}
                        fullWidth
                        required
                        size='small'
                        name={FormField.CompanyName}
                        value={values[FormField.CompanyName]}
                        label='Company Name'
                        onChange={handleChange}
                        onBlur={handleBlur}
                        InputProps={{
                            endAdornment: reviewableStatus
                        }}
                    />
                )}
            />
            <OnboardingReviewableFormInput
                name={FormField.RegistrationNumber}
                values={values}
                renderLabel={() => 'Registration Number'}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                renderFormInput={({ reviewableStatus, ...restFormInput }) => (
                    <TextField
                        {...restFormInput}
                        {...getFormInputProps(FormField.RegistrationNumber, form)}
                        fullWidth
                        required
                        size='small'
                        name={FormField.RegistrationNumber}
                        value={values[FormField.RegistrationNumber]}
                        label='Registration Number'
                        onChange={handleChange}
                        onBlur={handleBlur}
                        InputProps={{
                            endAdornment: reviewableStatus
                        }}
                    />
                )}
            />
            <OnboardingReviewableFormInput
                name={FormField.CountryOfIncorporation}
                values={values}
                renderLabel={() => 'Country of Incorporation'}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                renderFormInput={({ inputRef, disabled, reviewableStatus }) => (
                    <CountryIso2
                        fullWidth
                        disableClearable
                        disabled={disabled}
                        value={values[FormField.CountryOfIncorporation] ?? ''}
                        onChange={onAutocompleteChange(FormField.CountryOfIncorporation)}
                        TextFieldProps={{
                            ...getFormInputProps(FormField.CountryOfIncorporation, form),
                            inputRef,
                            required: true,
                            name: FormField.CountryOfIncorporation,
                            label: 'Country of Incorporation',
                            InputProps: {
                                endAdornment: reviewableStatus
                            }
                        }}
                    />
                )}
            />
            <OnboardingReviewableFormInput
                name={FormField.YearEstablished}
                values={values}
                renderLabel={() => 'Year of Establishment'}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                renderFormInput={({ inputRef, disabled, reviewableStatus }) => (
                    <LocalizationProvider
                        dateAdapter={AdapterMoment}
                    >
                        <DatePicker
                            disableFuture
                            label='Year of Establishment'
                            views={['year']}
                            disabled={disabled}
                            slotProps={{
                                textField: {
                                    ...getFormInputProps(FormField.YearEstablished, form),
                                    inputRef,
                                    name: FormField.YearEstablished,
                                    size: 'small',
                                    required: true,
                                    fullWidth: true,
                                    onBlur: handleBlur,
                                    InputProps: {
                                        endAdornment: reviewableStatus
                                    }
                                }
                            }}
                            value={values[FormField.YearEstablished]}
                            onChange={value => {
                                setFieldValue(FormField.YearEstablished, value);
                                setTimeout(() => setFieldTouched(FormField.YearEstablished, true, true));
                            }}
                        />
                    </LocalizationProvider>
                )}
            />
            <OnboardingReviewableFormInput
                name={FormField.WebsiteUrl}
                values={values}
                renderLabel={() => 'Website URL'}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                renderFormInput={({ reviewableStatus, ...restFormInput }) => (
                    <UrlTextField
                        {...restFormInput}
                        {...getFormInputProps(FormField.WebsiteUrl, form)}
                        fullWidth
                        required
                        size='small'
                        name={FormField.WebsiteUrl}
                        value={values[FormField.WebsiteUrl]}
                        label='Website URL'
                        onChange={handleChange}
                        onBlur={handleBlur}
                        InputProps={{
                            endAdornment: reviewableStatus
                        }}
                    />
                )}
            />
            <OnboardingReviewableFormInput
                name={FormField.PrimaryLocation}
                values={values}
                renderLabel={() => 'Primary Location'}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                renderFormInput={({ reviewableStatus, ...restFormInput }) => (
                    <TextField
                        {...restFormInput}
                        {...getFormInputProps(FormField.PrimaryLocation, form)}
                        fullWidth
                        required
                        size='small'
                        name={FormField.PrimaryLocation}
                        value={values[FormField.PrimaryLocation]}
                        label='Primary Location'
                        onChange={handleChange}
                        onBlur={handleBlur}
                        InputProps={{
                            endAdornment: reviewableStatus
                        }}
                    />
                )}
            />
            <OnboardingReviewableFormInput
                name={FormField.CountriesOfBusinessActivity}
                values={values}
                renderLabel={() => 'Countries of Business Activity'}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                renderFormInput={({ inputRef, disabled, reviewableStatus }) => (
                    <CountryIso2
                        multiple
                        fullWidth
                        disableClearable
                        disabled={disabled}
                        value={values[FormField.CountriesOfBusinessActivity]}
                        onChange={onAutocompleteChange(FormField.CountriesOfBusinessActivity)}
                        TextFieldProps={{
                            ...getFormInputProps(FormField.CountriesOfBusinessActivity, form),
                            inputRef,
                            name: FormField.CountriesOfBusinessActivity,
                            label: 'Countries of Business Activity *',
                            InputProps: {
                                endAdornment: reviewableStatus
                            }
                        }}
                    />
                )}
            />
            <Typography
                marginTop={2}
                variant='h6'
                gutterBottom
            >
                Which E-Commerce Platforms are you currently using?
            </Typography>
            <OnboardingReviewableFormInput
                name={FormField.Platforms}
                values={values}
                renderLabel={() => 'E-commerce Platforms in Use'}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                renderFormInput={({ disabled, reviewableStatus }) => (
                    <StatefulSelector
                        multiple
                        fullWidth
                        disablePortal
                        disabled={disabled}
                        size='small'
                        layout={null}
                        loading={isBootstrapDataLoading}
                        selectOptions={selectECommerceShoppingPlatforms}
                        value={values[FormField.Platforms]}
                        onChange={onAutocompleteChange(FormField.Platforms)}
                        TextFieldProps={{
                            ...getFormInputProps(FormField.Platforms, form),
                            label: 'E-commerce Platforms in Use',
                            name: FormField.Platforms,
                            InputProps: {
                                endAdornment: reviewableStatus
                            }
                        }}
                    />
                )}
            />
        </OnboardingForm>
    );
};

export default BusinessInformation;
